"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/Card"
import styles from "./ServicioLayout.module.css"
import "./Servicios.css"
import { useLocation, useNavigate, Link } from "react-router-dom"

function ServicioLayout({ servicioData }) {
  const servicioClass = `servicio-${servicioData.id}`
  const contentSectionRef = useRef(null)
  const titleRef = useRef(null)
  const navigate = useNavigate()
  // Usar useLocation de React Router
  const location = useLocation()

  // Estado para controlar la carga de la página
  const [pageLoaded, setPageLoaded] = useState(false)

  // Estado para controlar la velocidad de animación
  const [transitionSpeed, setTransitionSpeed] = useState("1.5s")

  // Estado para ocultar completamente las imágenes durante el cambio
  const [imagesHidden, setImagesHidden] = useState(false)

  // Modificar el estado inicial para incluir una propiedad que indique si la sección es visible
  const [scrollState, setScrollState] = useState({
    scrollPosition: 0,
    animationStep: 0,
    isInSection: false,
    lockScroll: false,
    lastScrollY: 0,
    scrollDirection: null,
    sectionVisible: false, // Nueva propiedad para controlar la visibilidad
  })

  // Definir resetHeroAnimation dentro del componente en lugar de recibirlo como prop
  const resetHeroAnimation = useCallback(() => {
    const heroBackground = document.querySelector(`.${styles.heroBackground}`)
    const heroContent = document.querySelector(`.${styles.heroContent}`)

    if (heroBackground && heroContent) {
      // Reset animation classes first
      heroBackground.classList.remove(styles.animateBackground)
      heroContent.classList.remove(styles.animateContent)

      // Force a reflow to ensure animations can be triggered again
      void heroBackground.offsetWidth
      void heroContent.offsetWidth

      // Start with content hidden
      heroContent.style.opacity = "0"

      // Trigger the background animation after a small delay
      setTimeout(() => {
        heroBackground.classList.add(styles.animateBackground)

        // Show content after background animation completes
        const animationDuration = 1000 // 1 second (match this with CSS)
        setTimeout(() => {
          heroContent.style.opacity = "1"
          heroContent.classList.add(styles.animateContent)
        }, animationDuration)
      }, 50) // Small delay to ensure DOM is ready
    }
  }, [])

  // Función para resetear completamente las animaciones
  const resetAllAnimations = useCallback(() => {
    // Primero, ocultar completamente las imágenes
    setImagesHidden(true)
    setPageLoaded(false)

    // Establecer una transición más lenta para el cambio entre servicios
    setTransitionSpeed("3s")

    // Resetear el estado
    setScrollState((prev) => ({
      scrollPosition: 0,
      animationStep: 0,
      isInSection: false,
      lockScroll: false,
      lastScrollY: 0,
      scrollDirection: null,
      sectionVisible: false,
    }))

    // Resetear la animación del hero
    resetHeroAnimation()

    // Programar la secuencia de animación:
    // 1. Ocultar las imágenes por completo
    // 2. Posicionar las imágenes fuera de la vista
    // 3. Permitir que la página se cargue
    // 4. Iniciar las animaciones

    // Permitir que las imágenes se oculten completamente primero
    setTimeout(() => {
      // Ahora colocar las imágenes en posición inicial (fuera de la vista)
      const leftImage = document.querySelector(`.${styles.leftColumn} .${styles.contentImage}`)
      const rightImage = document.querySelector(`.${styles.rightColumn} .${styles.contentImage}`)

      if (leftImage) {
        leftImage.style.transform = "translateX(-100%)"
        leftImage.style.opacity = "0"
      }

      if (rightImage) {
        rightImage.style.transform = "translateX(100%)"
        rightImage.style.opacity = "0"
      }

      // Ya no necesitamos mantener las imágenes ocultas, ahora están posicionadas fuera
      setImagesHidden(false)

      // Programar que la página se considere cargada después del cambio de vista
      setTimeout(() => {
        setPageLoaded(true)
      }, 500) // Retraso para que primero se muestre la página completamente
    }, 100) // Pequeño retraso para asegurar que las imágenes se oculten primero
  }, [resetHeroAnimation])

  // Efecto para resetear animaciones en cambio de ruta o cuando cambia servicioData
  useEffect(() => {
    console.log("Servicio cambiado a:", servicioData.id)

    // Resetear todas las animaciones
    resetAllAnimations()

    // Desregistrar y volver a registrar el IntersectionObserver
    if (contentSectionRef.current) {
      const currentRef = contentSectionRef.current

      // Forzar un reflow para asegurar que las animaciones se reinicien correctamente
      void currentRef.offsetWidth
    }
    
    // Scroll al inicio de la página
    window.scrollTo(0, 0)
  }, [resetAllAnimations, location, servicioData.id])

  // Efecto para establecer pageLoaded en true después de la carga inicial
  useEffect(() => {
    // En la carga inicial, establecer la velocidad de transición normal
    setTransitionSpeed("1.5s")

    // En la carga inicial, establecer pageLoaded como true después de un pequeño retraso
    const timeoutId = setTimeout(() => {
      setPageLoaded(true)
    }, 300)

    // Asegurar que la página siempre comience desde arriba
    window.scrollTo(0, 0)

    return () => clearTimeout(timeoutId)
  }, [])

  // Efecto adicional para asegurar que la página siempre comience desde arriba
  // cuando se monta el componente o se recarga la página
  useEffect(() => {
    // Scroll al inicio de la página al cargar
    window.scrollTo(0, 0)
    
    // También escuchar el evento de recarga de página
    const handleBeforeUnload = () => {
      // Guardar la posición en sessionStorage para después de la recarga
      sessionStorage.setItem('scrollToTop', 'true')
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)
    
    // Verificar si debemos desplazarnos hacia arriba después de una recarga
    if (sessionStorage.getItem('scrollToTop') === 'true') {
      window.scrollTo(0, 0)
      sessionStorage.removeItem('scrollToTop')
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  // Reemplazar el efecto que maneja el scroll con un IntersectionObserver
  useEffect(() => {
    if (!contentSectionRef.current) return

    console.log("Configurando IntersectionObserver para servicio:", servicioData.id)

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && pageLoaded) {
          console.log("Sección visible para servicio:", servicioData.id)

          // Añadir un retraso adicional antes de mostrar las imágenes después de que la sección sea visible
          setTimeout(() => {
            setScrollState((prev) => ({
              ...prev,
              sectionVisible: true,
              isInSection: true,
            }))
          }, 200) // Pequeño retraso adicional para el efecto visual
        } else {
          setScrollState((prev) => ({
            ...prev,
            isInSection: false,
            // No reseteamos sectionVisible aquí para mantener las imágenes visibles después de pasar la sección
          }))
        }
      },
      {
        threshold: 0.2, // Cuando al menos 20% de la sección es visible
      },
    )

    observer.observe(contentSectionRef.current)

    return () => {
      console.log("Limpiando IntersectionObserver para servicio:", servicioData.id)
      if (contentSectionRef.current) {
        observer.unobserve(contentSectionRef.current)
      }
    }
  }, [servicioData.id, pageLoaded]) // Añadir pageLoaded como dependencia

  // Hero animation effect - mantener este efecto para la carga inicial
  useEffect(() => {
    resetHeroAnimation()

    // Cleanup function
    return () => {
      const heroBackground = document.querySelector(`.${styles.heroBackground}`)
      const heroContent = document.querySelector(`.${styles.heroContent}`)

      if (heroBackground) {
        heroBackground.classList.remove(styles.animateBackground)
      }

      if (heroContent) {
        heroContent.classList.remove(styles.animateContent)
        heroContent.style.opacity = "0"
      }
    }
  }, [resetHeroAnimation])

  const handleMouseMove = (event) => {
    if (!titleRef.current) return

    const { clientX, clientY } = event
    const { left, top, width, height } = titleRef.current.getBoundingClientRect()

    // Calculate the position of the mouse relative to the title element
    const x = clientX - left
    const y = clientY - top

    // Get all characters in the title
    const chars = titleRef.current.querySelectorAll(".char")

    // Apply transform to each character based on distance from cursor
    chars.forEach((char) => {
      const charRect = char.getBoundingClientRect()
      const charX = charRect.left + charRect.width / 2 - left
      const charY = charRect.top + charRect.height / 2 - top

      // Calculate distance from cursor to this character
      const distance = Math.sqrt(Math.pow(x - charX, 2) + Math.pow(y - charY, 2))

      // Maximum distance to affect (in pixels)
      const maxDistance = 150

      // Maximum lift amount (in pixels)
      const maxLift = 20

      // Calculate lift amount based on distance (closer = more lift)
      // Use a smoother curve with Math.pow for more natural movement
      const lift = distance < maxDistance ? maxLift * Math.pow(1 - distance / maxDistance, 2) : 0

      // Apply transform with a slight delay based on character position
      // This creates a wave-like effect
      setTimeout(() => {
        char.style.transform = `translateY(-${lift}px)`
        char.style.textShadow = lift > 0 ? `0 ${lift / 2}px ${lift}px rgba(0,0,0,0.3)` : ""
      }, Math.random() * 50) // Small random delay for more natural movement
    })
  }

  // Función mejorada para manejar cuando el ratón deja el título
  const handleMouseLeave = () => {
    if (!titleRef.current) return

    // Obtener todos los caracteres del título
    const chars = titleRef.current.querySelectorAll(".char")
    
    // Resetear todas las transformaciones de caracteres con un retraso escalonado
    chars.forEach((char, index) => {
      // Añadir un pequeño retraso escalonado para una animación de reset más suave
      setTimeout(() => {
        char.style.transform = "translateY(0)"
        char.style.textShadow = ""
      }, index * 10) // Pequeño retraso escalonado para un reset más natural
    })
  }

  // Initialize the title with spans on component mount
  useEffect(() => {
    if (titleRef.current) {
      const text = servicioData.titulo || "" // Asegurarse de que hay texto
      titleRef.current.innerHTML = "" // Limpiar el contenido actual

      // Dividir el texto en palabras
      const words = text.split(" ")

      // Para cada palabra
      words.forEach((word, wordIndex) => {
        // Crear un contenedor para la palabra
        const wordSpan = document.createElement("span")
        wordSpan.className = "word"

        // Para cada carácter en la palabra
        for (let i = 0; i < word.length; i++) {
          const charSpan = document.createElement("span")
          charSpan.className = "char"
          charSpan.textContent = word[i]
          charSpan.style.display = "inline-block"
          // Transición más suave y rápida
          charSpan.style.transition = "transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), text-shadow 0.15s ease"
          // Asegurar que las transformaciones 3D sean suaves
          charSpan.style.willChange = "transform, text-shadow"
          charSpan.style.backfaceVisibility = "hidden"
          wordSpan.appendChild(charSpan)
        }

        // Añadir la palabra al título
        titleRef.current.appendChild(wordSpan)

        // Añadir un espacio después de cada palabra (excepto la última)
        if (wordIndex < words.length - 1) {
          const spaceSpan = document.createElement("span")
          spaceSpan.className = "space"
          spaceSpan.innerHTML = "&nbsp;"
          spaceSpan.style.display = "inline-block"
          spaceSpan.style.width = "0.35em" // Espacio más ancho
          titleRef.current.appendChild(spaceSpan)
        }
      })
    }
  }, [servicioData.titulo])

  const handleAgendarCita = () => {
    navigate("/contactanos")
  } 

  return (
    <div className={`${styles.servicioLayout} ${servicioClass}`}>
      {/* Botón Volver añadido */}
      <Link to="/" className="volver-btn">
        ← Volver
      </Link>
      
      {/* Hero Section */}
      <div className={`${styles.hero} ${servicioClass}`}>
        <div
          className={styles.heroBackground}
          // style={{ backgroundImage: `url(${servicioData.imagenHero || "/placeholder.svg"})` }}
        ></div>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <h1
            ref={titleRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className={styles.titleHover}
          >
            {servicioData.titulo}
          </h1>
          <h3 className={styles.subtitulo}>{servicioData.titulo1}</h3>
          <h2 className={styles.doctor}>{servicioData.doctor}</h2>
        </div>
      </div>

      {/* Main Content - Tres columnas */}
      <div className={styles.content} ref={contentSectionRef}>
        {/* Columna izquierda - Primera imagen */}
        <div className={styles.leftColumn}>
          <div className={styles.imageContainer}>
            {/* Modificar el estilo de la imagen izquierda */}
            <img
              src={servicioData.imagen1 || "/placeholder.svg"}
              alt={`${servicioData.titulo} 1`}
              className={styles.contentImage}
              style={{
                transform: `translateX(${scrollState.sectionVisible && pageLoaded ? "0" : "-100%"})`,
                opacity: scrollState.sectionVisible && pageLoaded ? 1 : 0,
                transition: `transform ${transitionSpeed} ease-in-out, opacity ${transitionSpeed} ease-in-out`,
                display: imagesHidden ? "none" : "block", // Ocultar completamente la imagen durante el cambio
              }}
            />
          </div>
        </div>

        {/* Columna central - Texto */}
        <div className={styles.centerColumn}>
          {servicioData.pregunta && (
            <h3 className={styles.pregunta} dangerouslySetInnerHTML={{ __html: servicioData.pregunta }}></h3>
          )}

          {/* Usar dangerouslySetInnerHTML para el contenido con formato HTML */}
          {typeof servicioData.descripcion === "string" &&
          servicioData.descripcion.includes("<") &&
          servicioData.descripcion.includes(">") ? (
            <div className={styles.descripcion} dangerouslySetInnerHTML={{ __html: servicioData.descripcion }} />
          ) : (
            <p>{servicioData.descripcion}</p>
          )}

          {servicioData.explicacion && <p>{servicioData.explicacion}</p>}
          {servicioData.explicacionAdicional && <p>{servicioData.explicacionAdicional}</p>}

          {/* Modificar el indicador visual de progreso para que se active todo al mismo tiempo */}
          <div className={styles.scrollIndicator}>
            <div className={`${styles.step} ${scrollState.sectionVisible && pageLoaded ? styles.active : ""}`}></div>
            <div className={`${styles.step} ${scrollState.sectionVisible && pageLoaded ? styles.active : ""}`}></div>
            <div className={`${styles.step} ${scrollState.sectionVisible && pageLoaded ? styles.active : ""}`}></div>
          </div>
        </div>

        {/* Columna derecha - Segunda imagen */}
        <div className={styles.rightColumn}>
          <div className={styles.imageContainer}>
            {/* Modificar el estilo de la imagen derecha */}
            <img
              src={servicioData.imagen2 || "/placeholder.svg"}
              alt={`${servicioData.titulo} 2`}
              className={styles.contentImage}
              style={{
                transform: `translateX(${scrollState.sectionVisible && pageLoaded ? "0" : "100%"})`,
                opacity: scrollState.sectionVisible && pageLoaded ? 1 : 0,
                transition: `transform ${transitionSpeed} ease-in-out, opacity ${transitionSpeed} ease-in-out`,
                display: imagesHidden ? "none" : "block", // Ocultar completamente la imagen durante el cambio
              }}
            />
          </div>
        </div>
      </div>

      {/* Resto del componente sin cambios */}
      {servicioData.infoCards && (
        <div className={styles.infoCardsContainer}>
          {servicioData.infoCards.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              <h2 className={styles.rowTitle}>{section.sectionTitle}</h2>
              <div className={styles.infoCardsGrid}>
                {section.cards.map((card, cardIndex) => (
                  <Card key={cardIndex} className={styles.infoCard}>
                    <CardHeader>
                      <CardTitle>{card.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={styles.texto} dangerouslySetInnerHTML={{ __html: card.value }}></p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Post Operatorio Section - Cambiado para mostrar imagen en lugar de video */}
      {servicioData.postOperatorio && (
        <div className={styles.postOperatorio}>
          {servicioData.postOperatorio.videoUrl && (
            <div className={styles.videoContainer}>
              {/* Reemplazado iframe por imagen manteniendo misma propiedad videoUrl */}
              <img
                className={styles.video}
                src={servicioData.postOperatorio.videoUrl || "/placeholder.svg"}
                alt="Post operatorio"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          )}
          <div className={styles.postOperatorioContent}>
            <h2>{servicioData.postOperatorio.titulo}</h2>
            <p>{servicioData.postOperatorio.descripcion}</p>
            <div className={styles.pasos}>
              {servicioData.postOperatorio.pasos.map((paso, index) => (
                <div key={index} className={styles.paso}>
                  <div className={styles.pasoNumero}>{paso.numero}</div>
                  <div className={styles.pasoContenido}>
                    <div className={styles.pasoTitulo}>{paso.titulo}</div>
                    <div className={styles.pasoTexto} dangerouslySetInnerHTML={{ __html: paso.texto }} />
                  </div>
                </div>
              ))}
            </div>
            <button className={styles.agendarCita} onClick={handleAgendarCita}>AGENDAR CITA</button>
          </div>
        </div>
      )}

      {/* Contenido Extra */}
      {servicioData.contenidoExtra?.map((seccion, index) => (
        <div key={index} className={styles.contenidoExtra}>
          {seccion.titulo && <h2>{seccion.titulo}</h2>}
          {seccion.descripcion && <p>{seccion.descripcion}</p>}
          {seccion.imagenes && (
            <div className={styles.imagenesGrid}>
              {seccion.imagenes.map((imagen, imgIndex) => (
                <img
                  key={imgIndex}
                  src={imagen || "/placeholder.svg"}
                  alt={`${seccion.titulo} ${imgIndex + 1}`}
                  className={styles.imagen}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default ServicioLayout